import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

// Animation variants
const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: 0.2 },
  },
};

/**
 * Base Modal component with framer-motion animations
 */
export const AnimatedModal = ({
  isOpen,
  onClose,
  children,
  width = "85%",
  maxWidth = "1200px",
  maxHeight = "95vh",
  zIndex = 5000,
  forceOnTop = false,
  fullScreenMobile = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isFullScreen = fullScreenMobile && isMobile;
  const modalWidth = isFullScreen ? "100%" : isMobile ? "95%" : width;

  // Create a portal to render the modal at the root level of the DOM
  // This ensures it's outside of any stacking context limitations
  const modalContent = isOpen && (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: forceOnTop ? 9999 : zIndex,
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(3px)",
      }}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          width: modalWidth,
          maxWidth: isFullScreen ? "100%" : maxWidth,
          maxHeight: isFullScreen ? "100%" : maxHeight,
          height: isFullScreen ? "100%" : undefined,
          backgroundColor: "#fff",
          borderRadius: isFullScreen ? 0 : "12px",
          overflow: "hidden",
          boxShadow: isFullScreen ? "none" : "0 10px 40px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {children}
      </motion.div>
    </Box>
  );

  // Use createPortal to append the modal directly to document.body
  // This ensures it's always on top regardless of parent elements' z-index
  return isOpen ? createPortal(modalContent, document.body) : null;
};

/**
 * Modal header component with customizable actions and content
 */
export const ModalHeader = ({
  title,
  icon: Icon,
  onClose,
  actions = [],
  customContent = null,
  rightContent = null,
  borderBottom = true,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        px: 3,
        borderBottom: borderBottom ? "1px solid rgba(0,0,0,0.08)" : "none",
        background: "linear-gradient(145deg, #ffffff, #f5f7fa)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {customContent ? (
          customContent
        ) : (
          <>
            {Icon && <Icon color="primary" sx={{ mr: 1.5 }} />}
            <Typography variant="h6" fontWeight="600">
              {title}
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Render right content if provided */}
        {rightContent}

        {/* Render custom action buttons */}
        {actions.map((action, index) => (
          <motion.div
            key={`action-${index}`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {action}
          </motion.div>
        ))}

        {onClose && (
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <IconButton onClick={onClose} color="default">
              <CloseIcon />
            </IconButton>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

/**
 * Modal content component with framer-motion animations
 */
export const ModalContent = ({ children, padding = 3 }) => {
  return (
    <Box
      sx={{
        p: padding === 0 ? 0 : padding,
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <motion.div variants={contentVariants} initial="hidden" animate="visible">
        {children}
      </motion.div>
    </Box>
  );
};

/**
 * Modal footer component
 */
export const ModalFooter = ({ children, borderTop = true }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        p: 2,
        borderTop: borderTop ? "1px solid rgba(0,0,0,0.08)" : "none",
        background: "#f9f9f9",
      }}
    >
      {children}
    </Box>
  );
};
